/**
 * Agent Service
 *
 * API client for AI code generation, refactoring, and explanation.
 */
import { api } from './api';

export type AgentAction =
  | 'generate'
  | 'refactor'
  | 'explain'
  | 'fix'
  | 'test'
  | 'document'
  | 'optimize';

export interface CodeActionRequest {
  action: AgentAction;
  request: string;
  file_path?: string;
  file_content?: string;
  selection?: string;
  language?: string;
  model?: string;
  stream?: boolean;
}

export interface CodeActionResponse {
  action: AgentAction;
  content: string;
  file_path?: string;
  language?: string;
  tokens_used?: number;
}

export interface GenerateComponentRequest {
  name: string;
  description: string;
  props?: Array<{ name: string; type?: string; description?: string }>;
  template?: string;
}

export interface GeneratePageRequest {
  name: string;
  description: string;
  features?: string[];
  layout?: string;
}

export interface GenerateApiRequest {
  endpoint: string;
  method?: string;
  description: string;
}

export interface GeneratedCode {
  name?: string;
  endpoint?: string;
  method?: string;
  content: string;
  language: string;
  file_path: string;
}

export interface TemplateInfo {
  name: string;
  description: string;
  variables: string[];
}

export interface TemplatesResponse {
  templates: Record<string, TemplateInfo[]>;
}

export interface TemplateRequest {
  category: string;
  template: string;
  variables: Record<string, string>;
}

export const agentService = {
  /**
   * Execute a code action (non-streaming).
   */
  async action(request: CodeActionRequest): Promise<CodeActionResponse> {
    return api.post<CodeActionResponse>('/agent/action', {
      ...request,
      stream: false,
    });
  },

  /**
   * Execute a code action with streaming. Yields content strings as the
   * model produces them; throws on backend error envelope. Server protocol:
   * `data: {"content":"..."}\n\n` per chunk + `data: {"error":"..."}\n\n` on
   * exception (E.17, 2026-04-29; replaced E.13's broken-since-inception
   * raw-string protocol).
   */
  async *streamAction(
    request: CodeActionRequest
  ): AsyncGenerator<string, void, unknown> {
    const generator = api.stream<{ content?: string; error?: string }>(
      '/agent/action',
      { ...request, stream: true }
    );

    for await (const chunk of generator) {
      if (chunk.error) {
        throw new Error(chunk.error);
      }
      if (chunk.content) {
        yield chunk.content;
      }
    }
  },

  /**
   * Generate a React component.
   */
  async generateComponent(
    request: GenerateComponentRequest
  ): Promise<GeneratedCode> {
    return api.post<GeneratedCode>('/agent/generate/component', request);
  },

  /**
   * Generate a React page.
   */
  async generatePage(request: GeneratePageRequest): Promise<GeneratedCode> {
    return api.post<GeneratedCode>('/agent/generate/page', request);
  },

  /**
   * Generate an API endpoint.
   */
  async generateApi(request: GenerateApiRequest): Promise<GeneratedCode> {
    return api.post<GeneratedCode>('/agent/generate/api', request);
  },

  /**
   * Get list of available templates.
   */
  async getTemplates(): Promise<TemplatesResponse> {
    return api.get<TemplatesResponse>('/agent/templates');
  },

  /**
   * Get template details.
   */
  async getTemplate(
    category: string,
    name: string
  ): Promise<{
    name: string;
    description: string;
    category: string;
    variables: string[];
    template: string;
  }> {
    return api.get(`/agent/templates/${category}/${name}`);
  },

  /**
   * Generate code using a template.
   */
  async useTemplate(
    request: TemplateRequest
  ): Promise<{ template: string; content: string; language: string }> {
    return api.post('/agent/templates/use', request);
  },

  /**
   * Explain code.
   */
  async explainCode(
    code: string,
    options?: { file_path?: string; language?: string }
  ): Promise<{ explanation: string }> {
    return api.post('/agent/explain', {
      code,
      ...options,
    });
  },

  /**
   * Fix code issues.
   */
  async fixCode(
    code: string,
    options?: { error?: string; file_path?: string; language?: string }
  ): Promise<{ fixed_code: string; file_path?: string }> {
    return api.post('/agent/fix', {
      code,
      ...options,
    });
  },
};

export default agentService;
